import { CaseDocumentActions } from '../../redux/actions/caseDocumentActions';
import { UploadActions } from '../../redux/actions/uploadActions';
import { HTTPContentType } from '../api/useApi';
import { CaseDocument } from '../types';

export interface FileUploadInfo {
    caseId: string;
    fileId: string;
    fileName: string;
    file?: File;
    fileSize: number;
    progress: number;
    uploadTime: number;
    uploadStarted: boolean;
    uploadComplete: boolean;
    otherUpload?: boolean;
    milestoneId?: string;
    taskId?: string;
    eventId?: string;
}

export class FileUploadManager {
    private static instance: FileUploadManager;
    private static accountId: string;

    private oneMB: number = 1024 * 1024;
    private fiveMB: number = 5 * this.oneMB;
    private fourMB: number = 4 * this.oneMB;
    private twoMB: number = 2 * this.oneMB;

    private NUM_FILES_CONCURRENT: number = 2;
    private MAX_CHUNK_THREADS: number = 20;
    private numChunkThreads =
        Math.min(this.MAX_CHUNK_THREADS, navigator.hardwareConcurrency * 2) / this.NUM_FILES_CONCURRENT;

    // private initUrl: string = '/api/CaseDocument/initDoc';
    // private uploadUrl: string = '';
    // private commitUrl: string = '';

    //private fileProcessors: number = 0;

    private _isProcessing: boolean = false;
    public get isProcessing() {
        return this._isProcessing;
    }
    private set isProcessing(value: boolean) {
        this._isProcessing = value;
    }

    //private files: Record<string, File> = {};

    private fileQueue: FileUploadInfo[] = [];
    public get fileQueueCount(): number {
        return this.fileQueue.length;
    }

    private milestoneId?: string;
    private taskId?: string;
    private eventId?: string;

    public fileUploads: FileUploadInfo[] = [];
    public get fileUploadsCount(): number {
        return this.fileUploads.length;
    }

    private static uploadActions?: UploadActions;
    private static caseDocumentActions?: CaseDocumentActions;

    private constructor() {}

    public static getInstance(
        caseDocumentActions?: CaseDocumentActions,
        uploadActions?: UploadActions,
        accountId?: string,
    ): FileUploadManager {
        if (!FileUploadManager.instance) {
            FileUploadManager.instance = new FileUploadManager();
        }
        if (!FileUploadManager.uploadActions) FileUploadManager.uploadActions = uploadActions;
        if (!FileUploadManager.caseDocumentActions) FileUploadManager.caseDocumentActions = caseDocumentActions;

        if (!!accountId && accountId != FileUploadManager.accountId) {
            FileUploadManager.accountId = accountId;
            //FileUploadManager.instance.clearAllUploadingFiles();
        }

        return FileUploadManager.instance;
    }

    private getHTTPHeaders(contentType?: string): Record<string, string> {
        const headers: Record<string, string> = { Accept: contentType || HTTPContentType.json };
        if (contentType) headers['Content-Type'] = contentType;
        return headers;
    }

    private getInitUrl(caseId: string) {
        return `/api/CaseDocument/account/${FileUploadManager.accountId}/case/${caseId}/doc/init`;
    }
    private getUploadChunkUrl(caseId: string, fileId: string, index: number) {
        return `/api/CaseDocument/account/${FileUploadManager.accountId}/case/${caseId}/doc/${fileId}/uploadDocChunk/${index}`;
    }
    private getCommitUrl(caseId: string, fileId: string) {
        return `/api/CaseDocument/account/${FileUploadManager.accountId}/case/${caseId}/doc/${fileId}/commitDoc`;
    }
    private cleanUrl(url: string) {
        return url.replace(/^\\*["']+|["']+\\*$/g, '');
    }

    private async ensureSuccess(response: Response, action: string) {
        if (!response.ok) {
            const details = await response.text().catch(() => '');
            throw new Error(`${action} failed (${response.status}): ${details || response.statusText}`);
        }
    }

    private async uploadChunkAsync(url: string, chunk: Blob) {
        try {
            const response = await fetch(this.cleanUrl(url), {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/octet-stream',
                },
                body: chunk,
            });

            await this.ensureSuccess(response, 'Chunk upload');
        } catch (e) {
            throw new Error('Failed to upload chunk');
        }
    }
    private async measureUploadSpeedAsync(url: string, data1MB: Blob) {
        try {
            const startTime = performance.now();
            await this.uploadChunkAsync(url, data1MB);

            const endTime = performance.now();
            const uploadTime = (endTime - startTime) / 1000; // seconds
            const uploadSpeed = data1MB.size / uploadTime; // bytes per second

            return uploadSpeed;
        } catch (e) {
            throw new Error('Failed to measure upload speed');
        }
    }
    private setUploadStarted(fileId: string) {
        FileUploadManager.uploadActions?.setUploadStarted(fileId);
    }
    private updateFileUploadProgress(fileId: string, progress: number) {
        const safeProgress = Number.isFinite(progress) ? progress : 0;
        const clampedProgress = Math.min(1, Math.max(0, safeProgress));
        FileUploadManager.uploadActions?.updateFileUploadProgress(fileId, clampedProgress);
    }
    private updateFileUploadTime(fileId: string, uploadTime: number) {
        FileUploadManager.uploadActions?.updateFileUploadTime(fileId, uploadTime);
    }

    public setAccount(accountId: string) {
        if (accountId != FileUploadManager.accountId) {
            FileUploadManager.accountId = accountId;
        }
    }
    public setUploadActions(uploadActions: UploadActions) {
        if (!FileUploadManager.uploadActions) FileUploadManager.uploadActions = uploadActions;
    }

    public setMilestoneId(milestoneId?: string) {
        this.milestoneId = milestoneId;
    }
    public setTaskId(taskId?: string) {
        this.taskId = taskId;
    }
    public setEventId(eventId?: string) {
        this.eventId = eventId;
    }

    public queueFiles(caseId: string, files: File[]) {
        const fileUploadInfos: FileUploadInfo[] = [];
        const fileQueueInfos: FileUploadInfo[] = [];
        const queuedMilestoneId = this.milestoneId;
        const queuedTaskId = this.taskId;
        const queuedEventId = this.eventId;

        // Upload context applies only to this queue invocation.
        this.milestoneId = undefined;
        this.taskId = undefined;
        this.eventId = undefined;

        files.forEach((file) => {
            const fileUploadInfo: FileUploadInfo = {
                fileId: crypto.randomUUID(),
                caseId: caseId,
                fileName: file.name,
                fileSize: file.size,
                progress: 0,
                uploadTime: 0,
                uploadStarted: false,
                uploadComplete: false,
                milestoneId: queuedMilestoneId,
                taskId: queuedTaskId,
                eventId: queuedEventId,
            };
            const fileQueueInfo: FileUploadInfo = { ...fileUploadInfo, file: file };

            fileUploadInfos.push(fileUploadInfo);
            fileQueueInfos.push(fileQueueInfo);
        });
        FileUploadManager.uploadActions?.uploadFiles(fileUploadInfos);
        this.fileQueue.push(...fileQueueInfos);

        this.processFileQueueAsync();
    }

    private async processFileQueueAsync() {
        if (!!this.isProcessing) return;

        this.isProcessing = true;

        const executing = new Set<Promise<void>>();

        while (this.fileQueue.length > 0 || executing.size > 0) {
            // Start new uploads up to the limit
            while (this.fileQueue.length > 0 && executing.size < this.NUM_FILES_CONCURRENT) {
                const fileUploadInfo = this.fileQueue.shift();

                if (fileUploadInfo) {
                    const promise = this.uploadFileAsync(fileUploadInfo).finally(() => executing.delete(promise));

                    executing.add(promise);
                }
            }

            // Wait for at least one to complete before continuing
            if (executing.size > 0) {
                await Promise.race(executing);
            }
        }

        this.isProcessing = false;
        setTimeout(() => this.clearUploadedFiles(), 4000);
    }

    public initDocUploadAsync = async (fileUploadInfo: FileUploadInfo) => {
        //initialize upload, adding fileid to initUrl
        const response = await fetch(this.getInitUrl(fileUploadInfo.caseId), {
            method: 'POST',
            credentials: 'include',
            headers: this.getHTTPHeaders(HTTPContentType.json),
            body: JSON.stringify({
                caseId: fileUploadInfo.caseId,
                fileId: fileUploadInfo.fileId,
                filename: fileUploadInfo.fileName,
                isExpenseAttachment: !!fileUploadInfo.otherUpload,
                milestoneId: fileUploadInfo.milestoneId,
                taskId: fileUploadInfo.taskId,
                eventId: fileUploadInfo.eventId,
            }),
        });

        await this.ensureSuccess(response, 'Init doc upload');
    };

    public uploadFileAsync = async (fileUploadInfo: FileUploadInfo) => {
        const startUploadTime = performance.now();

        if (!FileUploadManager.accountId) {
            throw new Error('No accountId set for FileUploadManager');
        }

        this.setUploadStarted(fileUploadInfo.fileId);

        const caseId = fileUploadInfo.caseId;
        const fileId = fileUploadInfo.fileId;
        const file = fileUploadInfo.file;
        const fileSize = fileUploadInfo.fileSize;
        console.log(fileUploadInfo);

        if (!file) {
            throw new Error('No file found for upload');
        }

        //initialize upload, adding fileid to initUrl
        await this.initDocUploadAsync(fileUploadInfo);

        let chunkOffset = 0;
        let chunkIndex = 0;
        let uploadSpeed = 0;
        let chunkSize = this.oneMB;

        //Measure Upload Speed
        const firstChunk = file.slice(chunkOffset, chunkOffset + this.oneMB);

        //measure upload speed
        const uploadChunkUrl = this.getUploadChunkUrl(caseId, fileId, chunkIndex);
        uploadSpeed = await this.measureUploadSpeedAsync(uploadChunkUrl, firstChunk);

        chunkOffset += this.oneMB;
        chunkIndex++;

        //determine chunkSize
        if (uploadSpeed >= this.fiveMB)
            chunkSize = this.fourMB; // 5MB/s or faster = 4MB chunks
        else if (uploadSpeed >= this.oneMB) chunkSize = this.twoMB; // 1MB/s to 5MB/s = 2MB chunks

        const getProgress = (uploadedBytes: number): number => {
            if (fileSize <= 0) return 1;
            return uploadedBytes / fileSize;
        };

        let progress = firstChunk.size;

        this.updateFileUploadProgress(fileId, getProgress(progress));

        const processChunk = async (offset: number, index: number) => {
            const chunk = await file.slice(offset, offset + chunkSize);
            const processChunkUrl = this.getUploadChunkUrl(caseId, fileId, index);

            await this.uploadChunkAsync(processChunkUrl, chunk).then(() => {
                progress += chunk.size;
                this.updateFileUploadProgress(fileId, getProgress(progress));
            });
        };

        const executing: Promise<void>[] = [];

        //start chunking
        while (chunkOffset < fileSize) {
            if (executing.length < this.numChunkThreads) {
                const promise = processChunk(chunkOffset, chunkIndex).then(() => {
                    executing.splice(executing.indexOf(promise), 1);
                });

                executing.push(promise);

                chunkOffset += chunkSize;
                chunkIndex++;
            } else {
                await Promise.race(executing);
            }
        }

        // Wait for all chunk threads to finish uploading
        await Promise.all(executing);

        const commitUrl = this.getCommitUrl(caseId, fileId);
        const commitResponse = await fetch(commitUrl, {
            method: 'POST',
            credentials: 'include',
        });

        await this.ensureSuccess(commitResponse, 'Commit doc upload');
        this.updateFileUploadProgress(fileId, 1);

        //get the document and add to case document actions
        const newDoc: CaseDocument = await commitResponse.json();
        if (!newDoc.isExpenseAttachment && !!FileUploadManager.caseDocumentActions) {
            FileUploadManager.caseDocumentActions?.addOrReplaceCaseDocument(newDoc);
        }

        const endUploadTime = performance.now();
        const uploadTime = (endUploadTime - startUploadTime) / 1000;
        this.updateFileUploadTime(fileId, uploadTime);
    };

    public clearUpload(fileId: string) {
        this.fileQueue = this.fileQueue.filter((f) => f.fileId !== fileId);
        FileUploadManager.uploadActions?.clearUpload(fileId);
    }
    public clearUploadedFiles() {
        FileUploadManager.uploadActions?.clearUploadedFiles();
    }
    public clearAllUploadingFiles() {
        FileUploadManager.uploadActions?.clearAllFiles();
        this.fileQueue = [];
    }
}

export default FileUploadManager;
